import { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

/**
 * Admin · Price Pulse Approvals card.
 *
 * Renders on the Admin Overview. Shows every scan that landed in the
 * ``pending_approval`` bucket, plus a one-tap Publish / Reject.
 * Also exposes a "Run scan now" trigger to force a fresh scrape AND
 * a "Vinci Master Template" CSV/XLSX bulk uploader.
 */
export default function AdminPricePulseCard() {
  const { api } = useAuth();
  const [state, setState] = useState({ loading: true, scans: [] });
  const [busy, setBusy] = useState(null);
  const cementRef = useRef(null);
  const steelRef = useRef(null);

  const refresh = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const r = await api.get("/admin/price-pulse/pending");
      setState({ loading: false, scans: r.data.scans || [] });
    } catch { setState({ loading: false, scans: [] }); }
  };

  useEffect(() => { refresh(); }, []);

  const scanNow = async () => {
    setBusy("scan");
    try {
      const r = await api.post("/admin/price-pulse/scan-now");
      const s = r.data.scan || {};
      toast.success(`Fresh scan captured · ${s.items_count || 0} SKUs · status ${s.status}`);
      refresh();
    } catch (e) {
      toast.error(`Scan failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy(null); }
  };

  const downloadTemplate = (kind) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/price-pulse/template/${kind}.csv`;
    // api.get(url, {responseType:"blob"}) would work but we let the browser
    // handle auth via the interceptor by using axios directly through api.
    api.get(`/admin/price-pulse/template/${kind}.csv`, { responseType: "blob" })
      .then((r) => {
        const blobUrl = URL.createObjectURL(new Blob([r.data]));
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = `Vinci-Master-${kind === "cement" ? "Cement" : "Steel"}-Template.csv`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(blobUrl), 500);
      })
      .catch(() => toast.error("Template download failed"));
  };

  const downloadEasyFill = (kind) => {
    toast.info(`Building Easy-Fill ${kind} template · embedding 100+ brand logos…`);
    api.get(`/admin/price-pulse/template/easy-fill/${kind}.xlsx`, { responseType: "blob" })
      .then((r) => {
        const blobUrl = URL.createObjectURL(new Blob([r.data]));
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = `Vinci-EasyFill-${kind === "cement" ? "Cement" : "Steel"}-Template.xlsx`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(blobUrl), 500);
        toast.success("Easy-Fill template ready · just type the ₹");
      })
      .catch(() => toast.error("Easy-Fill template download failed"));
  };

  const uploadBulk = async () => {
    const cement = cementRef.current?.files?.[0];
    const steel = steelRef.current?.files?.[0];
    if (!cement && !steel) { toast.error("Attach at least one template file"); return; }
    const fd = new FormData();
    if (cement) fd.append("cement", cement);
    if (steel) fd.append("steel", steel);
    setBusy("upload");
    try {
      const r = await api.post("/admin/price-pulse/bulk-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const s = r.data.scan || {};
      const errs = s.errors || [];
      if (errs.length) {
        toast.warning(`Uploaded ${s.items_count} SKUs · ${errs.length} row error(s) — check pending scan for details`);
      } else {
        toast.success(`Uploaded ${s.items_count} SKUs · status ${s.status}`);
      }
      if (cementRef.current) cementRef.current.value = "";
      if (steelRef.current) steelRef.current.value = "";
      refresh();
    } catch (e) {
      toast.error(`Upload failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy(null); }
  };

  const publish = async (sid) => {
    setBusy(sid);
    try {
      const r = await api.post(`/admin/price-pulse/${sid}/publish`);
      toast.success(`Published · ${r.data.published_items} SKUs now live on Shop`);
      refresh();
    } catch (e) {
      toast.error(`Publish failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy(null); }
  };

  const reject = async (sid) => {
    setBusy(sid);
    try {
      await api.post(`/admin/price-pulse/${sid}/reject`);
      toast.success("Scan rejected");
      refresh();
    } catch (e) {
      toast.error(`Reject failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy(null); }
  };

  return (
    <section className="bg-[#0B0B0B] border border-white/10 rounded-sm p-6" data-testid="admin-price-pulse-card">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#B87333]">Vinci · Price Pulse</div>
          <h3 className="mt-1 font-serif text-2xl text-[#F5F5F0]">Daily Price Approvals</h3>
          <p className="mt-1 font-body text-[11px] text-[#8A8A82]">
            Every scan or upload lands here as pending. Publish to go live on the Shop; Reject to discard.
          </p>
        </div>
        <button type="button" onClick={scanNow} disabled={busy === "scan"}
          className="px-3 py-1.5 text-[10px] uppercase tracking-[0.24em] border border-[#D8B23E]/50 text-[#D8B23E] hover:bg-[#D8B23E] hover:text-[#0A0A0A] transition-colors rounded-sm disabled:opacity-40"
          data-testid="admin-price-pulse-scan-now">
          {busy === "scan" ? "Scanning…" : "Run scan now"}
        </button>
      </div>

      <div className="mb-5 border border-white/10 rounded-sm p-4 bg-black/40">
        <div className="flex items-center justify-between gap-4 mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#B87333]">Vinci Master Template</div>
            <div className="font-body text-[11px] text-[#8A8A82] mt-0.5">
              Upload CSV or XLSX. Missing GST columns are auto-calculated at 18% (Cement × 1.18, Steel × 1.18). Per-bar / per-MT are computed with the Vinci TMT formula.
            </div>
          </div>
          <div className="flex gap-2 shrink-0 flex-wrap">
            <button type="button" onClick={() => downloadEasyFill("cement")}
              className="px-3 py-1 text-[9px] uppercase tracking-[0.2em] bg-[#D8B23E] text-black hover:bg-[#c8a336] rounded-sm"
              data-testid="admin-price-pulse-easyfill-cement">✨ Easy-Fill Cement</button>
            <button type="button" onClick={() => downloadEasyFill("steel")}
              className="px-3 py-1 text-[9px] uppercase tracking-[0.2em] bg-[#D8B23E] text-black hover:bg-[#c8a336] rounded-sm"
              data-testid="admin-price-pulse-easyfill-steel">✨ Easy-Fill Steel</button>
            <button type="button" onClick={() => downloadTemplate("cement")}
              className="px-2 py-1 text-[9px] uppercase tracking-[0.2em] border border-white/20 text-[#C9C9C2] hover:border-[#D8B23E]/60 rounded-sm"
              data-testid="admin-price-pulse-template-cement">Cement CSV</button>
            <button type="button" onClick={() => downloadTemplate("steel")}
              className="px-2 py-1 text-[9px] uppercase tracking-[0.2em] border border-white/20 text-[#C9C9C2] hover:border-[#D8B23E]/60 rounded-sm"
              data-testid="admin-price-pulse-template-steel">Steel CSV</button>
          </div>
        </div>
        <div className="mt-2 text-[10px] font-body text-[#B87333]/80">
          🇮🇳 <strong>Easy-Fill</strong> templates come with 100+ Indian cement &amp; steel brands pre-loaded, region-grouped, with logos already embedded — you only type the rate.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A8A82]">
            Cement file
            <input ref={cementRef} type="file" accept=".csv,.xlsx,.xls"
              className="mt-1 w-full font-body text-[11px] text-[#F5F5F0] file:mr-3 file:py-1 file:px-3 file:border file:border-white/15 file:bg-transparent file:text-[#D8B23E] file:rounded-sm file:cursor-pointer"
              data-testid="admin-price-pulse-file-cement" />
          </label>
          <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A8A82]">
            Steel file
            <input ref={steelRef} type="file" accept=".csv,.xlsx,.xls"
              className="mt-1 w-full font-body text-[11px] text-[#F5F5F0] file:mr-3 file:py-1 file:px-3 file:border file:border-white/15 file:bg-transparent file:text-[#D8B23E] file:rounded-sm file:cursor-pointer"
              data-testid="admin-price-pulse-file-steel" />
          </label>
        </div>
        <div className="mt-3 flex justify-end">
          <button type="button" onClick={uploadBulk} disabled={busy === "upload"}
            className="px-3 py-1.5 text-[10px] uppercase tracking-[0.24em] bg-[#D8B23E] text-[#0A0A0A] hover:bg-[#c8a336] transition-colors rounded-sm disabled:opacity-40"
            data-testid="admin-price-pulse-upload">
            {busy === "upload" ? "Uploading…" : "Upload templates"}
          </button>
        </div>
      </div>

      {state.loading && (
        <div className="font-body text-[11px] text-[#8A8A82]">Loading…</div>
      )}
      {!state.loading && state.scans.length === 0 && (
        <div className="border border-white/5 p-4 rounded-sm font-body text-[11px] text-[#8A8A82]" data-testid="admin-price-pulse-empty">
          Nothing pending. The next scan fires at 06:00 IST — or tap "Run scan now" / upload templates above.
        </div>
      )}
      <div className="space-y-3">
        {state.scans.map((s) => (
          <div key={s.id} className="border border-white/10 p-4 rounded-sm flex items-center justify-between gap-4"
               data-testid={`admin-price-pulse-scan-${s.id.slice(0,8)}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <div className="font-mono text-xs text-[#F5F5F0]">{s.id.slice(0, 8)}…</div>
                {s.source === "manual_upload" && (
                  <span className="text-[9px] uppercase tracking-[0.2em] text-[#D8B23E] border border-[#D8B23E]/50 px-1.5 py-0.5 rounded-sm">
                    Manual Upload
                  </span>
                )}
              </div>
              <div className="mt-1 font-body text-[11px] text-[#8A8A82]">
                {new Date(s.started_at).toLocaleString()} · <span className="text-[#F5F5F0]">{s.items_count}</span> SKUs
                {s.errors?.length ? <span className="ml-2 text-[#E5484D]">· {s.errors.length} error(s)</span> : null}
              </div>
              {s.errors?.slice(0, 3).map((err, ei) => (
                <div key={ei} className="mt-1 font-mono text-[10px] text-[#E5484D]/80 truncate">· {err}</div>
              ))}
            </div>
            <div className="flex gap-2 shrink-0">
              <button type="button" onClick={() => publish(s.id)} disabled={busy === s.id}
                className="px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] bg-[#2FB999] text-[#0A0A0A] rounded-sm hover:bg-[#25a086] disabled:opacity-40"
                data-testid={`admin-price-pulse-publish-${s.id.slice(0,8)}`}>
                {busy === s.id ? "…" : "Publish"}
              </button>
              <button type="button" onClick={() => reject(s.id)} disabled={busy === s.id}
                className="px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] border border-[#E5484D]/40 text-[#E5484D] rounded-sm hover:bg-[#E5484D]/10 disabled:opacity-40"
                data-testid={`admin-price-pulse-reject-${s.id.slice(0,8)}`}>
                Reject
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
